// EditSalesAgreementProductPriceModal.tsx

import React, { PropsWithChildren, useEffect, useState } from 'react'
import { Box, InputAdornment, Stack, styled, Typography } from '@mui/material'
import { SaleAgreementPromoProduct } from './types'
import { Button } from '../../../../components/Button'
import { CostPriceList } from '../../../cost-price/components/CostPrice'
import TestIds from '../../../../testIds'
import Modal from '../../../../components/Modal'
import Input from '../../../../components/Input'
import { RevertChangesButton } from '../../../products/components/ProductCard/RevertChangesButton'
import { formatPrice, generateImageUrl } from '../../../../util/helpers'
import PlaceholderImage from '../../../../assets/images/image-placeholder.png'
import ImageWithPlaceholder from '../../../../components/ImageWithPlaceholder'
import {
  PriceStyle,
  ProductPriceDetails,
} from '../../../products/components/ProductCard/ProductPriceDetails'

const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}))

interface EditSalesAgreementProductPriceModalProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  item?: SaleAgreementPromoProduct
  onApplyEditedPrice: (newPrice: number, item: SaleAgreementPromoProduct) => void
  onRevertEditedPrice: (item: SaleAgreementPromoProduct) => void
}

export const EditSalesAgreementProductPriceModal = ({
  open,
  setOpen,
  item,
  onApplyEditedPrice,
  onRevertEditedPrice,
}: PropsWithChildren<EditSalesAgreementProductPriceModalProps>) => {
  const [unitPrice, setUnitPrice] = useState<string | undefined>(undefined)
  const [unitPriceError, setUnitPriceError] = useState<string | undefined>('')

  // Ensure the unit price is for the current item.
  useEffect(() => {
    if (item) {
      setUnitPrice(item.unitPrice.toFixed(2))
    }
  }, [item, open])

  const handleCloseModal = () => {
    setOpen(false)
  }

  const revertChanges = () => {
    if (item) {
      setUnitPrice(undefined)
      onRevertEditedPrice(item)
    }
    handleCloseModal()
  }

  const applyEditedPrice = () => {
    if (!unitPrice) {
      setUnitPriceError('Please enter a valid unit price')
      return
    }

    const inputValue = parseFloat(unitPrice)
    if (inputValue <= 0) {
      setUnitPriceError('Please enter a unit price greater than $0')
      return
    } else {
      setUnitPriceError(undefined)
    }

    if (unitPrice && item) {
      onApplyEditedPrice(parseFloat(unitPrice), item)
    }
    handleCloseModal()
  }

  const priceList = React.useMemo(() => {
    if (item) {
      return <CostPriceList productId={item.productId} /> // Adjust as per your data structure
    }
  }, [item])

  const footer = React.useMemo(() => {
    return (
      <StyledButton
        data-testid={TestIds.feat.editPrice.saveChanges}
        variant='contained'
        onClick={applyEditedPrice}
      >
        Save changes
      </StyledButton>
    )
  }, [applyEditedPrice])

  if (!item) return null

  return (
    <Modal
      title='Edit unit price'
      open={open}
      handleClose={handleCloseModal}
      footer={footer}
      testId={TestIds.feat.editPrice.modal}
    >
      <Stack spacing={4} pb={2}>
        <Stack direction={'row'} gap={2}>
          <Box height={'80px'} width={'80px'}>
            <ImageWithPlaceholder
              src={generateImageUrl(item.itemNumber)}
              alt=''
              placeholderImageUrl={PlaceholderImage}
              size={160}
            />
          </Box>
          <Stack gap={1}>
            <Typography variant='h3'>{item.description}</Typography>
            <ProductPriceDetails
              items={[
                {
                  type: PriceStyle.Discounted,
                  label: 'Your price:',
                  gstLabel: 'GST excl.',
                  value: formatPrice(unitPrice ? parseFloat(unitPrice) : item.unitPrice),
                  testId: TestIds.feat.product.card.discountedPrice,
                },
                {
                  type: PriceStyle.RRP,
                  label: 'RRP:',
                  gstLabel: 'GST excl.',
                  value: formatPrice(item.unitPrice),
                  testId: TestIds.feat.product.card.nationalPrice,
                },
              ]}
              size='Medium'
              sx={{
                gap: 2,
              }}
            />
          </Stack>
        </Stack>
        {priceList}
        <Input
          label='Unit price (GST excl.):'
          placeholder={unitPrice}
          value={unitPrice}
          setValue={(v) => {
            const value = v?.valueOf().toString()
            // Allow empty input
            if (!value) {
              setUnitPrice('')
              return
            }

            // Validate the input using regex
            const regex = /^\d{0,9999}(\.\d{0,2})?$/
            if (regex.test(value) || value === '') {
              setUnitPrice(value)
            }
          }}
          errorMessage={unitPriceError}
          outlineInputProps={{
            type: 'number',
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          }}
          type='number'
          testId={TestIds.feat.editPrice.unitPrice}
        />
        <RevertChangesButton onClick={revertChanges} />
      </Stack>
    </Modal>
  )
}
